import { ImageConfig, AppState, ImageData } from './reducers/index';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { createAsyncThunk, createAction } from '@reduxjs/toolkit';
import _ from 'lodash'
import { Filters, Product, Unit } from './services/assortment.service';
import { store } from '.';
import { ArticleIndex } from './ArticleIndex';
import { AppMode } from './constants';
import { get } from './communication';

export type AsyncAction<ReturnType = void> = ThunkAction<ReturnType, AppState, void, Action<string>>
export type Dispatch = ThunkDispatch<AppState, void, Action<string>>

const fetchText = url => new Promise<string>((resolve, reject) => {
    let json_req = new XMLHttpRequest()
    json_req.onload = () => {
        resolve(json_req.responseText)
    }
    json_req.onerror = () => {
        reject(json_req.status)
    }
    json_req.open('GET', url, true)
    json_req.send()
})

export const JSON_LOAD_PENDING = 'JSON_LOAD_PENDING'
export const JSON_LOAD_FAILURE = 'JSON_LOAD_FAILURE'
export const JSON_LOAD_SUCCESS = 'JSON_LOAD_SUCCESS'

export const SYSTEMS_JSON = 'SYSTEMS_JSON'
export const CONFIG_JSON = 'CONFIG_JSON'

export const loadJSON = (path, type) => async dispatch => {
    dispatch({
        type: JSON_LOAD_PENDING
    })
    try {
        let json;
        if (type === SYSTEMS_JSON) {
            json = await get(path);
        } else {
            json = await fetchText(path);
        }
        dispatch({
            type: JSON_LOAD_SUCCESS,
            payload: {
                type: type,
                json: json
            }
        })
    } catch (e) {
        dispatch({
            type: JSON_LOAD_FAILURE,
            error: true,
        })
        dispatch(setMessageAction('Błąd przy ładowaniu danych...'))
    }
}

export const LANG_CHANGE = 'LANG_CHANGE'

export const langChange = (lang) => ({
    type: LANG_CHANGE,
    payload: {
        lang: lang
    }
})


export const EDIT_ARTICLE_PROPS = 'EDIT_ARTICLE_PROPS'

export const editArticleProps = (index, value) => ({
    type: EDIT_ARTICLE_PROPS,
    payload: {
        index: index,
        value: value,
    }
})

export const SET_MESSAGE = 'SET_MESSAGE'

export const setMessageAction = (msg) => ({
    type: SET_MESSAGE,
    payload: {
        msg: msg
    }
})

export const IMAGE_ERROR = 'IMAGE_ERROR'

export const imageErrorAction = (msg) => ({
    type: IMAGE_ERROR,
    payload: {
        msg: msg
    }
})

const loadImage = (path: string) => new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()
    image.src = path
    image.onerror = () => {
        reject('image failed to load')
    }
    image.onload = () => {
        resolve(image)
    }
})

export const fetchImage = async (path: string, signal?: AbortSignal): Promise<HTMLImageElement> => {
    const response = await fetch(path, { signal })
    const blob = await response.blob()

    let image: HTMLImageElement
    try {
        image = await loadImage(URL.createObjectURL(blob))
    } finally {
        URL.revokeObjectURL(image.src)
    }
    return image
}

export const loadAndProcessImage = createAsyncThunk<{ imgData: ImageData | null, url: string }, ImageConfig, { state: AppState, rejectValue: { url: string } }>(
    'loadAndProcessImage',
    async (image, { dispatch, signal }) => {
        try {
            const origImg = await fetchImage(image.path, signal)

            if (signal.aborted) throw new Error('aborted')

            // crop image to remove useless transparent borders that hurt canvas performance

            const origCanvas = document.createElement('canvas')
            origCanvas.width = origImg.width
            origCanvas.height = origImg.height
            const origCtx = origCanvas.getContext('2d')
            origCtx.drawImage(origImg, 0, 0)
            const origData = origCtx.getImageData(0, 0, origImg.width, origImg.height)

            let minX, minY, maxX, maxY
            minX = minY = Number.MAX_SAFE_INTEGER
            maxX = maxY = Number.MIN_SAFE_INTEGER

            const rowLen = origData.width * 4

            for (let i = 0; i < origData.data.length; i += 4) {
                const alpha = origData.data[i + 3];
                if (alpha !== 0) {
                    const y = Math.trunc(i / rowLen)
                    const x = (i % rowLen) >> 2
                    minX = Math.min(minX, x)
                    minY = Math.min(minY, y)
                    maxX = Math.max(maxX, x)
                    maxY = Math.max(maxY, y)
                }
            }

            const newW = maxX - minX
            const newH = maxY - minY

            const cropCanvas = document.createElement('canvas')
            cropCanvas.width = newW
            cropCanvas.height = newH
            const cropCtx = cropCanvas.getContext('2d')
            cropCtx.drawImage(origCanvas, minX, minY, newW, newH, 0, 0, newW, newH)

            return {
                imgData: {
                    image: cropCanvas,
                    offsetX: minX,
                    offsetY: minY,
                    origWidth: origData.width,
                    origHeight: origData.height,
                },
                url: image.path,
            }
        } catch (e) {
            if (signal.aborted) throw e
            dispatch(imageErrorAction("Błąd przy ładowaniu obrazka: " + image.path))
            return { imgData: null, url: image.path }
        }
    }
)

export const loadImagesForViewProducts = createAsyncThunk<void, void, { state: AppState }>(
    'loadImagesForView',
    async (xd, { dispatch, getState, signal }) => {
        dispatch({
            type: SET_IMAGES_LOADING,
            payload: true
        });
        
        const products = getState().filteredAssortment.products;
        const images = getState().images;
        const listImages = getState().listImages;

        const promises = _(products)
            .map(prd => !images.has(`${store.getState().config.articlesImgPath}${prd.file}`) ? dispatch(loadAndProcessImage(createImageConfigForProduct(prd))) : null)
            .value()
            .filter(el => el !== null)
        const promises2 = _(products)
            .map(prd => !listImages.has(`${store.getState().config.articlesImgPath}${prd.listFile}`) ? dispatch(loadListImage(`${store.getState().config.articlesImgPath}${prd.listFile}`)) : null)
            .value()
            .filter(el => el !== null)

        signal.addEventListener('abort', () => {
            for (const promise of promises) {
                promise.abort()
            }
        })
        signal.addEventListener('abort', () => {
            for (const promise of promises2) {
                promise.abort()
            }
        })

        await Promise.all(promises)
        await Promise.all(promises2)

        dispatch({
            type: SET_IMAGES_LOADING,
            payload: false
        });
    }
)

export const createImageConfigForProduct = (prd: Product): ImageConfig => {
    const imgConfig: ImageConfig = {
        fileName: prd.file,
        path: `${store.getState().config.articlesImgPath}${prd.file}`,
        filter: prd.color === '' ? 'none' : 'color',
        img_pos: {
            x: prd.posX,
            y: prd.posY,
            z: prd.posZ,
        },
        show: (prd.posX < 0 && prd.posY < 0) ? false : true,
        index: new ArticleIndex(1, 1, 1, 1)
    }

    return imgConfig;
}

export const loadListImage = createAsyncThunk<{ reqUrl: string, objUrl: string }, string, { state: AppState }>(
    'loadListImage',
    async (url, { dispatch, signal }) => {
        try {
            const response = await fetch(url, { signal });
            const blob = await response.blob();
            return {
                reqUrl: url,
                objUrl: URL.createObjectURL(blob),
            }
        } catch (e) {
            if (signal.aborted) throw e
            dispatch(imageErrorAction("Błąd przy ładowaniu obrazka: " + url))
            return ({ reqUrl: url, objUrl: null })
        }
    },
)

export const setAbortFn = createAction<() => void>('setAbortFn')

export const SELECT_ARTICLE = 'SELECT_ARTICLE'

export const selectArticleAction = (id: number) => ({
    type: SELECT_ARTICLE,
    payload: id
})

export const SELECT_ARTICLE_GROUP = 'SELECT_ARTICLE_GROUP'

export const selectArticleGroup = group => ({
    type: SELECT_ARTICLE_GROUP,
    payload: {
        group,
    }
})

export const TOGGLE_NAV = 'TOGGLE_NAV'

export const toggleNav = () => ({
    type: TOGGLE_NAV
})

export const MODAL = 'MODAL'

export const modal = (imgSrc) => ({
    type: MODAL,
    payload: {
        src: imgSrc
    }
})

export const TOGGLE_PART_IMAGE = 'TOGGLE_PART_IMAGE'

export const togglePartImage = () => ({
    type: TOGGLE_PART_IMAGE
})

export const UPDATE_IMG_POS = 'UPDATE_IMG_POS'

export const updateImgPos = (id, pos) => ({
    type: UPDATE_IMG_POS,
    payload: {
        id: id,
        pos: pos
    }
})

export const LOAD_OLD_ORDER = 'LOAD_OLD_ORDER'

export interface ImportOrderItem {
    product_id: string
    product_qty: number
    systemData?: Filters
    product_unit: Unit
}

export const loadOldOrder = (oldOrder: ImportOrderItem[]) => {
    const tmpOrder: ImportOrderItem[] = []
    if (oldOrder.length > 0 && !oldOrder[0].systemData) {
        for (const product of oldOrder) {
            const index = tmpOrder.findIndex(e => e.product_id === product.product_id)
            if (index > -1) {
                tmpOrder[index].product_qty += product.product_qty
            } else {
                tmpOrder.push({ 
                    product_id: product.product_id, 
                    product_qty: Number(product.product_qty),
                    product_unit: product.product_unit
                })
            }
        }
    } else {
        tmpOrder.push(...oldOrder);
    }

    return {
        type: LOAD_OLD_ORDER,
        payload: {
            oldOrder: tmpOrder
        }
    }
}

export const RESET_ORDER = 'RESET_ORDER'

export const resetOrder = () => {
    const systemData = store.getState().filters;
    return {
        type: RESET_ORDER,
        payload: {
            systemData
        }
    }
}

// --------------------------------------------

export const SET_FILTER = 'ADD_FILTER'
export const SET_IS_FILTERING = 'SET_IS_FILTERING'
export const DROP_FILTER = 'DROP_FILTER'
export const CLEAR_FILTERS = 'CLEAR_FILTERS'
export const SET_FILTERED_ASSORTMENT = 'SET_FILTERED_ASSORTMENT'
export const SET_PRODUCTS_TO_SHOW = 'SET_PRODUCTS_TO_SHOW'
export const SET_IMAGES_LOADING = 'SET_IMAGES_LOADING'
export const EDIT_ORDER = 'EDIT_ORDER'

export const editOrder = (id: number, count: number, unit: Unit) => {
    return {
        type: EDIT_ORDER,
        payload: {
            id,
            count,
            unit
        }
    }
}

export const SET_APP_MODE = 'SET_APP_MODE'
export const setAppMode = (mode: AppMode) => {
    return {
        type: SET_APP_MODE,
        payload: mode
    }
}